import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import WebViewAppBar from '../../components/WebViewAppBar';
import styles from './MembershipUpgradeSelection.module.scss';
import { usePurchaseSubscriptionContext } from '../PurchaseSubscription/context/PurchaseSubscriptionContext';
import Button from '../../components/Button';
import { SubscriptionOption } from '../../components/SubscriptionOptionDetails/SubscriptionOptionDetails';
import { useSubscriptionCartMode } from '../../lib/cartModes';
import { gqlBillingCadenceToBillingCadence } from '../../lib/subscription';
import { cartActions } from '../../reducers/subscriptionCart';
import { useSubscriptionChooserGroups } from '../../components/SubscriptionOptions/SubscriptionOptions';
import Chooser from '../../components/Chooser';
import { PurchaseSubscriptionCheckoutState } from '../PurchaseSubscription/PurchaseSubscriptionCheckout/PurchaseSubscriptionCheckout';
import AppPaths from '../../AppPaths';
import classNames from 'classnames';
import { useLocale } from '../../contexts/LocaleContext';

function getRateUnitToDisplay(displayedRateUnit: string, t: any): string {
  if (displayedRateUnit === 'one time') {
    return t('one_time');
  } else if (displayedRateUnit === 'month') {
    return t('monthly_uppercase');
  } else {
    return t('weekly');
  }
}

function getSubscriptionDescription(subscription: SubscriptionOption, t: any): string {
  if (subscription.sku === 'sub-monthly-1m-001') {
    return t('monthly');
  } else if (subscription.sku === 'sub-monthly-3m-001') {
    return t('3_month_membership');
  } else if (subscription.sku === 'sub-monthly-6m-001') {
    return t('6_month_membership');
  } else if (subscription.sku === 'sub-monthly-12m-001' || subscription.sku === 'sub-1y-001') {
    return t('1_year_membership');
  } else if (subscription.sku === 'sub-monthly-24m-001' || subscription.sku === 'sub-2y-001') {
    return t('2_year_membership');
  } else if (subscription.sku === 'sub-3y-001') {
    return t('3_year_membership');
  } else {
    return subscription.name;
  }
}

export default function MembershipUpgradeSelection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { device } = usePurchaseSubscriptionContext();
  const { cartSelector } = useSubscriptionCartMode();
  const cart = useSelector(cartSelector);
  const { currency } = useLocale();

  const petName = device?.pet?.name;
  const avatarUrl = device?.pet?.photos.first?.image.fullSize ?? '/dog_placeholder.png';

  const applicableSubscriptionProducts = device.purchasableSubscriptionOptions.reduce(
    (applicableProducts: SubscriptionOption[], subscriptionOption) => {
      if (!subscriptionOption.supportPlus) {
        return applicableProducts;
      }

      applicableProducts.push({
        ...subscriptionOption,
        billingCadence: gqlBillingCadenceToBillingCadence(subscriptionOption.billingCadence),
      });
      return applicableProducts;
    },
    [],
  );

  const selectedSubscriptionSku = useMemo(() => {
    const cartItem = Object.values(cart.cartItems)[0];
    if (!cartItem) {
      return undefined;
    }

    return cartItem.lineItem.sku;
  }, [cart.cartItems]);

  const selectedSubscriptionOption = useMemo(() => {
    return applicableSubscriptionProducts.find((option) => option.sku === selectedSubscriptionSku);
  }, [applicableSubscriptionProducts, selectedSubscriptionSku]);

  const chooserGroups = useSubscriptionChooserGroups({
    subscriptionProducts: applicableSubscriptionProducts,
  });

  // Navigate to the checkout page
  const goToCheckout = useCallback(() => {
    history.push({
      pathname: AppPaths.MembershipUpgrade.Checkout(device.moduleId),
      state: { subscriptionOption: selectedSubscriptionOption } as PurchaseSubscriptionCheckoutState,
      search: window.location.search,
    });
  }, [device.moduleId, history, selectedSubscriptionOption]);
  const checkoutDisabled = selectedSubscriptionOption ? false : true;
  const selectedPlanText = selectedSubscriptionOption && getSubscriptionDescription(selectedSubscriptionOption, t);

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <WebViewAppBar />
        <div className={styles.container}>
          <h1 className={styles.mainHeader}>{t('upgrade_your_membership')}</h1>
          <div className={styles.sectionHeaderContainer}>
            <h2 className={styles.sectionHeader}>{t('current_plan_for_pet_name', { petName })}</h2>
          </div>
          <div className={styles.currentPlanContainer}>
            <img className={styles.petAvatar} src={avatarUrl} alt={t('pet_avatar')} />
            <div className={styles.currentPlanDescription}>
              {!device.subscription ? (
                <p>{t('no_current_subscription')}</p>
              ) : !device.subscription.subscriptionOption ? (
                <p>{t('unknown_subscription_plan')}</p>
              ) : (
                <>
                  <div className={styles.currentPlanUnit}>
                    {getRateUnitToDisplay(device.subscription.subscriptionOption.displayedRateUnit, t)}
                  </div>
                  <div className={styles.currentPlanRate}>
                    {device.subscription.subscriptionOption.displayedRate}
                    {'/'}
                    {device.subscription.subscriptionOption.displayedRateUnit}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={styles.sectionHeaderContainer}>
            <h2 className={styles.sectionHeader}>{t('select_a_new_plan')}</h2>
            <h3 className={styles.sectionSubheader}>{t('prepay_and_save')}</h3>
          </div>
          {applicableSubscriptionProducts.length === 0 ? (
            <p>{t('no_plans_available_please_contact_support_if_you_think_this_is_an_error')}</p>
          ) : (
            <>
              {' '}
              <Chooser
                onSelect={(selectedSku) => {
                  if (!selectedSku) {
                    return;
                  }

                  dispatch(
                    cartActions.setCartItem({
                      moduleId: device.moduleId,
                      sku: selectedSku,
                      currency,
                    }),
                  );
                }}
                selectedOption={selectedSubscriptionSku}
                groups={chooserGroups}
              />
            </>
          )}
        </div>
        {applicableSubscriptionProducts.length > 0 && (
          <div className={classNames(styles.stickyCTA, styles.productAction)}>
            {selectedPlanText && <div className={styles.selectedPlanText}>{selectedPlanText}</div>}
            <Button
              className={styles.button}
              disabled={checkoutDisabled}
              onClick={() => {
                goToCheckout();
              }}
            >
              {t('continue')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
